import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, from, Observable, of } from "rxjs";
import { IDBPDatabase, openDB } from "idb";
import { switchMap } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class ImageCacheService {

  private dbSubject: BehaviorSubject<IDBPDatabase<any> | null>;

  constructor() {
    this.dbSubject = new BehaviorSubject<IDBPDatabase<any> | null>(null);

    openDB('image-cache-db', 1, {
      upgrade(db) {
        db.createObjectStore('images', { keyPath: 'id' });
      },
    }).then(db => {
      this.dbSubject.next(db);
    }).catch(error => {
      console.error('Failed to open IndexedDB:', error);
    });
  }

  private getDatabase(): Observable<IDBPDatabase<any>> {
    return this.dbSubject.asObservable().pipe(
      filter((db): db is IDBPDatabase<any> => !!db) // Отфильтровываем null и приводим тип
    );
  }

  cacheImage(id: string, blob: Blob): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.put('images', { id, blob })))
    );
  }

  getCachedImage(id: string): Observable<Blob | null> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.get('images', id))),
      switchMap(result => of(result ? result.blob : null))
    );
  }

  deleteImage(id: string): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.delete('images', id)))
    );
  }

  clearCache(): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.clear('images')))
    );
  }

  deleteOldImages(): Observable<void> {
    const expirationDate = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);

    return this.getDatabase().pipe(
      switchMap(db => {
        const transaction = db.transaction('images', 'readwrite');
        const store = transaction.objectStore('images');
        const request = store.openCursor();

        return from(request).pipe(
          switchMap(function processCursor(cursor): Observable<void> {
            if (!cursor) return of(undefined) as Observable<void>;

            const record = cursor.value;
            const recordDate = new Date(record.timestamp);

            let delete$ = of(undefined) as Observable<void>;
            if (recordDate < expirationDate) {
              delete$ = from(cursor.delete()).pipe(
                catchError(err => {
                  console.error('Failed to delete record:', err);
                  return of(undefined) as Observable<void>;
                })
              );
            }

            return delete$.pipe(
              switchMap(() => from(cursor.continue())),
              switchMap(cursor => processCursor(cursor))
            );
          }),
          finalize(() => transaction.done)
        );
      }),
      catchError(error => {
        console.error('Failed to delete old images:', error);
        return of(undefined) as Observable<void>;
      })
    );
  }

  runDailyCleanup(): void {
    const lastCleanup = localStorage.getItem('lastCleanup');
    const now = Date.now();

    if (!lastCleanup || now - parseInt(lastCleanup, 10) > 24 * 60 * 60 * 1000) {
      this.deleteOldImages().subscribe({
        next: () => {
          localStorage.setItem('lastCleanup', now.toString());
        },
        error: (error) => {
          console.error('Error while deleting old images:', error);
        }
      });
    }
  }
}
